import { useState, useEffect, useRef } from 'react';
import { ChevronDown, MapPin, X } from 'lucide-react';
import { useRegionStore } from '@/stores/regionStore';
import { useDebounce } from '@/hooks/useDebounce';
import type { Region } from '@/types';
import { cn } from '@/utils/cn';

interface RegionSelectorProps {
  value?: number | null;
  onChange: (region: Region | null) => void;
  governorate?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  showGovernorate?: boolean;
}

/**
 * RegionSelector Component
 * Dropdown selector for Kuwait regions with governorate grouping
 */
export const RegionSelector: React.FC<RegionSelectorProps> = ({
  value,
  onChange,
  governorate,
  placeholder = 'اختر المنطقة...',
  required = false,
  disabled = false,
  className = '',
  showGovernorate = true,
}) => {
  const { regions, fetchRegions, loading, error } = useRegionStore();
  const [searchQuery, setSearchQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Debounce search query to avoid excessive API calls
  const debouncedSearch = useDebounce(searchQuery, 300);

  // Initial load of regions when component mounts or dropdown opens
  useEffect(() => {
    // Don't reload if user is actively searching - let the search effect handle it
    if (debouncedSearch.trim()) {
      return;
    }

    const filters: any = { isActive: true };
    
    if (governorate) {
      filters.governorate = governorate;
    }

    // Load all regions on mount
    fetchRegions(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [governorate]);

  // Fetch regions with search filter when user types
  useEffect(() => {
    if (!debouncedSearch.trim()) {
      // If search is cleared, reload all regions
      const filters: any = { isActive: true };
      
      if (governorate) {
        filters.governorate = governorate;
      }
      
      fetchRegions(filters);
    } else {
      // Search with query - don't filter by governorate during search
      // This allows all matching regions to be shown
      const filters: any = {
        isActive: true,
        search: debouncedSearch.trim()
      };

      fetchRegions(filters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  // Filter regions by governorate client-side ONLY when NOT searching
  // When searching, show all results from backend to avoid hiding matches
  const filteredRegions = (governorate && !debouncedSearch.trim())
    ? regions.filter((region) => region.governorateEn === governorate)
    : regions;

  // Group regions by governorate
  const regionsByGovernorate = filteredRegions.reduce((acc, region) => {
    // Ensure governorateEn exists before grouping
    const govKey = region.governorateEn || 'Other';
    if (!acc[govKey]) {
      acc[govKey] = [];
    }
    acc[govKey].push(region);
    return acc;
  }, {} as Record<string, Region[]>);

  // Find selected region
  const selectedRegion = value
    ? regions.find((r) => r.id === value)
    : null;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (region: Region) => {
    onChange(region);
    setIsOpen(false);
    setSearchQuery('');
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(null);
    setSearchQuery('');
  };

  return (
    <div ref={dropdownRef} className={cn('relative', className)}>
      {/* Selector Button */}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={cn(
          'w-full flex items-center justify-between gap-2 px-4 py-2.5 rounded-lg border transition-all',
          'bg-dark-lighter border-gray-700 text-gray-200',
          'focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary',
          disabled && 'opacity-50 cursor-not-allowed',
          !disabled && 'hover:border-gray-600 cursor-pointer',
          error && 'border-red-500'
        )}
      >
        <div className="flex items-center gap-2 flex-1 text-right">
          <MapPin className="h-4 w-4 text-gray-400" />
          <span className={cn('truncate', !selectedRegion && 'text-gray-400')}>
            {selectedRegion ? (
              <>
                {selectedRegion.nameEn} - {selectedRegion.nameAr}
                {showGovernorate && (
                  <span className="text-xs text-gray-500 mr-2">
                    ({selectedRegion.governorateEn})
                  </span>
                )}
              </>
            ) : (
              placeholder
            )}
          </span>
        </div>
        <div className="flex items-center gap-1">
          {selectedRegion && !disabled && (
            <span
              onClick={handleClear}
              className="p-1 hover:bg-dark rounded transition-colors cursor-pointer"
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleClear(e as any);
                }
              }}
            >
              <X className="h-3 w-3 text-gray-400" />
            </span>
          )}
          <ChevronDown
            className={cn(
              'h-4 w-4 text-gray-400 transition-transform',
              isOpen && 'transform rotate-180'
            )}
          />
        </div>
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-dark-lighter border border-gray-700 rounded-lg shadow-lg max-h-96 overflow-hidden">
          {/* Search Input */}
          <div className="p-2 border-b border-gray-700 sticky top-0 bg-dark-lighter">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="ابحث عن منطقة..."
              className="w-full px-3 py-2 bg-dark border border-gray-700 rounded text-sm text-gray-200 placeholder-gray-500 focus:border-primary focus:outline-none"
              autoFocus
            />
          </div>

          {/* Regions List */}
          <div className="overflow-y-auto max-h-80">
            {loading ? (
              <div className="p-4 text-center text-gray-400 text-sm">
                جاري التحميل...
              </div>
            ) : filteredRegions.length === 0 ? (
              <div className="p-4 text-center text-gray-400 text-sm">
                لا توجد مناطق مطابقة
              </div>
            ) : (
              Object.entries(regionsByGovernorate).map(
                ([gov, govRegions]) => (
                  <div key={gov}>
                    {/* Governorate Header */}
                    <div className="px-3 py-2 bg-dark text-xs font-semibold text-primary sticky top-12 border-b border-gray-700">
                      {gov}
                    </div>
                    {/* Regions in Governorate */}
                    {govRegions.map((region) => (
                      <div
                        key={region.id}
                        role="option"
                        aria-selected={region.id === value}
                        tabIndex={0}
                        onClick={() => handleSelect(region)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleSelect(region);
                          }
                        }}
                        className={cn(
                          'w-full text-right px-4 py-2 hover:bg-dark transition-colors cursor-pointer',
                          'flex items-center justify-between gap-2',
                          region.id === value && 'bg-primary/10 text-primary'
                        )}
                      >
                        <span className="flex-1 text-sm">
                          {region.nameEn} - {region.nameAr}
                        </span>
                        {region.id === value && (
                          <span className="text-primary">✓</span>
                        )}
                      </div>
                    ))}
                  </div>
                )
              )
            )}
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="mt-1 text-xs text-red-500">{error}</div>
      )}

      {/* Required Indicator */}
      {required && !selectedRegion && (
        <div className="mt-1 text-xs text-gray-500">* مطلوب</div>
      )}
    </div>
  );
};
